/**
 * @file PlannerNoteModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description The planners' own note on an order (Cam, 2026-10-12): a
 *              reminder that stays on the planning board — shown on the row
 *              and in the Lorry day window — and never leaves it: not on the
 *              driver's paperwork, the mill emails or anything sent. Saved
 *              with the order's lorry day plan (transport.line_plans.
 *              planner_note, PUT /api/transport/planning/line-plans).
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { PLANNER_NOTE_MAX } from '../utils/lorryDay';
import './LorryDayWindow.css';

function NoteBody({ value, setValue, canEdit, readOnlyReason, saving, error }) {
  useSlideOutButton([saving]);
  return (
    <div className="pnm-body">
      <label htmlFor="planner-note-text" className="pnm-label">Note</label>
      <textarea
        id="planner-note-text"
        className="form-input pnm-text"
        rows={5}
        maxLength={PLANNER_NOTE_MAX}
        value={value}
        disabled={!canEdit || saving}
        placeholder="A reminder for whoever plans this day"
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      <p className="ldw-muted">
        {value.length}/{PLANNER_NOTE_MAX} · For the planners only: it shows on the board while planning and is never printed or sent.
      </p>
      {!canEdit && <p className="ldw-readonly">{readOnlyReason}</p>}
      {error && <p className="ldw-message ldw-message-error" role="alert">{error}</p>}
    </div>
  );
}

/**
 * @param {object}   props
 * @param {object}   props.order     the board row
 * @param {string}   props.note      the note it has now
 * @param {Function} props.onSave    note → Promise (throws with the reason)
 * @param {Function} props.onClose
 */
function PlannerNoteModal({ order, note, canEdit, readOnlyReason, onSave, onClose }) {
  const [value, setValue] = useState(note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setValue(note || ''); setError(null); }, [order, note]);
  if (!order) return null;
  const changed = value.trim() !== String(note || '').trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div><button className="btn-slideout" onClick={onClose} disabled={saving}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
        <span className="btn-label">{changed ? 'Cancel' : 'Close'}</span>
      </button></div>
      <div><button className={`btn-slideout${canEdit && note ? '' : ' btn-disabled'}`} disabled={!canEdit || !note || saving}
        title={!canEdit ? readOnlyReason : (note ? 'Remove the note' : 'There is no note to remove')}
        onClick={() => { setValue(''); }}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">delete</span></div>
        <span className="btn-label">Clear</span>
      </button></div>
      <div><button className={`btn-slideout${canEdit && changed ? '' : ' btn-disabled'}`} disabled={!canEdit || !changed || saving}
        title={!canEdit ? readOnlyReason : (changed ? 'Save the note' : 'Nothing has changed')}
        onClick={save}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
        <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
      </button></div>
    </div>
  );

  const ref = order.purchase_order_number || order.order_number || '';
  return (
    <Modal isOpen onClose={() => { if (!saving) onClose(); }} title={`Planner’s note${ref ? ` · ${ref}` : ''} · ${order.customer_name || ''}`} size="sm" className="planner-note-modal" footer={footer}>
      <NoteBody value={value} setValue={setValue} canEdit={canEdit} readOnlyReason={readOnlyReason} saving={saving} error={error} />
    </Modal>
  );
}

export default PlannerNoteModal;
